import {Component, AfterViewInit} from '@angular/core';
import {BaseComponent} from '../../../base/base.component';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {GetUnitiesGQL, Unity, User} from '../../../../generated/graphql';
import {UserService} from '../../../service/user.service';
import {DaycareService} from '../../../service/daycare.service';

/**
 * Fatia 5C — MANUTENÇÃO (formato canônico com IDs, §47.6.B/C). Fluxo oficial:
 * EXPORTAR → editar → IMPORTAR de volta. REJEITA planilha inconsistente (mostra
 * lista de erros acionável, sem plano). Válida → mostra o plano (incluir/alterar/
 * remover) com destaque na remoção e no alerta de remoção em massa (§47.6.B).
 * Só admin e local-manager; seletor de unidade só p/ admin.
 */
@Component({
    selector: 'app-daycare-import-maintenance',
    templateUrl: './daycare-import-maintenance.component.html',
    styleUrls: ['./daycare-import-maintenance.component.scss']
})
export class DaycareImportMaintenanceComponent extends BaseComponent implements AfterViewInit {

    user = new User();
    showUnitySelector = false; // só admin
    unities: Array<Unity> = [];
    unityId: string = null;

    file: File = null;
    fileName = '';
    preview: any = null;
    loading = false;

    constructor(public router: Router,
                public translate: TranslateService,
                public userService: UserService,
                private daycareService: DaycareService,
                private getUnitiesGQL: GetUnitiesGQL
    ) {
        super(router, translate);
    }

    ngAfterViewInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
                this.actionUserProfileBTN();
                if (this.actionUserProfile() === false) {
                    super.showMessage(this.translate.instant('atencao'), this.translate.instant('semacesso'), 'warning');
                    this.router.navigate(['/']);
                    return;
                }
                if (this.showUnitySelector) {
                    this.loadUnities();
                }
            }, error: err => super.onError(err)
        });
    }

    loadUnities() {
        this.getUnitiesGQL.watch().valueChanges.subscribe(({data}) => {
            this.unities = (data.unities || []) as Array<Unity>;
        }, error => super.onError(error));
    }

    onFileSelected(event) {
        const files = event.target.files;
        this.file = files && files.length ? files[0] : null;
        this.fileName = this.file ? this.file.name : '';
        this.preview = null;
    }

    private effectiveUnityId(): string {
        return this.showUnitySelector ? this.unityId : null;
    }

    private requireUnity(): boolean {
        if (this.showUnitySelector && !this.unityId) {
            super.showMessage(this.translate.instant('atencao'), this.translate.instant('daycare.selecioneunidade'), 'warning');
            return false;
        }
        return true;
    }

    // Baixar planilha atual (export) — fluxo oficial: exportar → editar → importar.
    downloadCurrent() {
        if (!this.requireUnity()) {
            return;
        }
        this.loading = true;
        this.daycareService.exportExcel(this.effectiveUnityId()).subscribe({
            next: (data: any) => {
                this.loading = false;
                super.downloadHandlerFile(data);
            }, error: (err) => {
                this.loading = false;
                super.onError(err);
            }
        });
    }

    analyze() {
        if (!this.requireUnity()) {
            return;
        }
        if (!this.file) {
            super.showMessage(this.translate.instant('atencao'), this.translate.instant('daycare.selecionearquivo'), 'warning');
            return;
        }
        this.loading = true;
        this.preview = null;
        this.daycareService.importMaintenancePreview(this.file, this.effectiveUnityId()).subscribe({
            next: (data: any) => {
                this.preview = data;
                this.loading = false;
            }, error: (err) => {
                this.loading = false;
                this.handleRestError(err);
            }
        });
    }

    // Agrupa os erros bloqueantes por `type` para exibir em seções.
    errorTypes(): Array<string> {
        if (!this.preview || !this.preview.errors) {
            return [];
        }
        return this.preview.errors
            .map((e) => e.type)
            .filter((t, i, arr) => arr.indexOf(t) === i);
    }

    errorsByType(type: string): Array<any> {
        if (!this.preview || !this.preview.errors) {
            return [];
        }
        return this.preview.errors.filter((e) => e.type === type);
    }

    confirmImport() {
        if (!this.preview || !this.preview.valid) {
            return;
        }
        const c = this.preview.counts || {create: 0, update: 0, delete: 0};
        const msg = this.translate.instant('daycare.resumomanutencao')
            .replace('{create}', c.create || 0)
            .replace('{update}', c.update || 0)
            .replace('{delete}', c.delete || 0);
        super.confirmMessage(this.translate.instant('atencao'), msg, () => this.apply());
    }

    private apply() {
        this.loading = true;
        this.daycareService.importMaintenanceApply(this.file, this.effectiveUnityId()).subscribe({
            next: (data: any) => {
                this.loading = false;
                if (data && data.valid === false) {
                    // A planilha mudou entre preview e apply e ficou inválida.
                    this.preview = data.preview || {valid: false, errors: data.errors || []};
                    super.showMessage(this.translate.instant('atencao'),
                        this.translate.instant('daycare.planilhainvalida'), 'warning');
                    return;
                }
                if (data && data.applied) {
                    const a = data.applied_counts || {};
                    const msg = this.translate.instant('daycare.manutencaosucesso')
                        .replace('{created}', this.sumCounts(a.created) + this.sumCounts(a.restored))
                        .replace('{updated}', this.sumCounts(a.updated))
                        .replace('{deleted}', this.sumCounts(a.deleted));
                    super.showMessage(this.translate.instant('success'), msg, 'success');
                    this.router.navigate(['/daycare']);
                    return;
                }
                super.showMessage(this.translate.instant('atencao'),
                    this.translate.instant('daycare.naogravado'), 'warning');
            }, error: (err) => {
                this.loading = false;
                this.handleRestError(err);
            }
        });
    }

    private sumCounts(c: any): number {
        if (!c) {
            return 0;
        }
        return (c.employees || 0) + (c.children || 0) + (c.guardians || 0);
    }

    private handleRestError(err: any) {
        if (err && err.status === 401) {
            this.router.navigate(['/login']);
            return;
        }
        const body = err && err.body ? err.body : err;
        const message = (body && body.message) ? body.message : this.translate.instant('daycare.erroimportacao');
        super.showMessage(this.translate.instant('atencao'), message, 'warning');
    }

    actionUserProfile(): boolean {
        switch (this.user.profile) {
            case 'admin':
                return true;
            case 'local-manager':
                return true;
            default:
                return false;
        }
    }

    actionUserProfileBTN() {
        this.showUnitySelector = this.user.profile === 'admin';
    }
}
